import { showDirectionsPopup } from '../utils/directions.js';
import { openSharePopup } from '../utils/share.js';
import { isFavorite, toggleFavorite } from '../utils/favorites.js';
import { MARKER_POPUP_DISPLAY, MARKER_POPUP_ACTION_BUTTONS, DOMAIN_NAME } from '/settings.js';
import { buildUrlForIds } from '../utils/url-utils.js';
import { MarkerPopupCarrousel } from './MarkerPopupCarrousel.js';
import { getCurrentLanguage, getText } from '../translations/translations.js';
// Blog imports are lazy-loaded when needed (only if BLOG_ENABLED is true)
import { createIconElement, createIconMarkup } from '../utils/icon.js';
import { replaceLinkShortcodes } from '../utils/link-shortcodes.js';


function sortTags(tags, searchedTags) {
  if (!Array.isArray(searchedTags) || searchedTags.length === 0) return tags;
  return [
    ...tags.filter(tag => searchedTags.includes(tag.slug)),
    ...tags.filter(tag => !searchedTags.includes(tag.slug))
  ];
}

/**
 * Converts day numbers (1-7) to translated day names
 * 1 = Sunday, 2 = Monday, 3 = Tuesday, 4 = Wednesday, 5 = Thursday, 6 = Friday, 7 = Saturday
 */
async function formatClosedDays(closedDaysValue) {
  if (!closedDaysValue || !closedDaysValue.toString().trim()) {
    return null;
  }

  const lang = getCurrentLanguage();
  const dayNumbers = closedDaysValue.toString()
    .split(',')
    .map(d => parseInt(d.trim()))
    .filter(d => !isNaN(d) && d >= 1 && d <= 7);

  if (dayNumbers.length === 0) {
    return null;
  }

  // Get day names from translations
  const dayNames = [];
  for (const dayNum of dayNumbers) {
    const dayKey = `spotInfo.day${dayNum}`;
    const dayName = await getText(dayKey, lang);
    dayNames.push(dayName);
  }

  // Join with commas and "and" for the last one if multiple
  let daysText;
  if (dayNames.length === 1) {
    daysText = dayNames[0];
  } else if (dayNames.length === 2) {
    daysText = `${dayNames[0]} ${await getText('spotInfo.and', lang)} ${dayNames[1]}`;
  } else {
    const lastDay = dayNames.pop();
    daysText = `${dayNames.join(', ')} ${await getText('spotInfo.and', lang)} ${lastDay}`;
  }

  return daysText;
}

async function buildMetadataBadges(spot, content) {
  const metadataContainer = content.querySelector('.marker-popup-metadata');
  if (!metadataContainer) return;

  const badges = [];

  // is_free
  if (spot.is_free === true || spot.is_free === 1) {
    const text = await getText('spotInfo.freeEntry');
    badges.push({ text, key: 'free' });
  } else if (spot.is_free === false || spot.is_free === 0) {
    const text = await getText('spotInfo.paidEntry');
    badges.push({ text, key: 'paid' });
  }

  // wheelchair
  if (spot.wheelchair === true || spot.wheelchair === 1) {
    const text = await getText('spotInfo.wheelchairAccessible');
    badges.push({ text, key: 'wheelchair' });
  }

  // petFriendly
  if (spot.petFriendly === true || spot.petFriendly === 1) {
    const text = await getText('spotInfo.petFriendly');
    badges.push({ text, key: 'pet' });
  }

  // closedDays - now handles day numbers (1-7) instead of text
  if (spot.closedDays) {
    const daysText = await formatClosedDays(spot.closedDays);
    if (daysText) {
      let translationKey = await getText('spotInfo.closedDays');
      translationKey = translationKey.replace('{days}', daysText);
      badges.push({ text: translationKey, key: 'closed' });
    }
  }

  // requiresPermit
  if (spot.requiresPermit === true || spot.requiresPermit === 1) {
    const text = await getText('spotInfo.requiresPermit');
    badges.push({ text, key: 'permit' });
  }

  // noPhotography
  if (spot.noPhotography === true || spot.noPhotography === 1) {
    const text = await getText('spotInfo.noPhotography');
    badges.push({ text, key: 'photography' });
  }

  // Only render if we have badges
  if (badges.length > 0) {
    // Create text content with middle dot separators
    const textContent = badges.map(badge => badge.text).join(' · ');

    // Check if distance exists - only show separator if both distance and metadata exist
    const distanceSpan = content.querySelector('.marker-popup-distance');
    if (distanceSpan) {
      // Both distance and metadata exist - add "|" separator with spacing
      metadataContainer.appendChild(document.createTextNode(' | '));
    }

    metadataContainer.appendChild(document.createTextNode(textContent));
  } else {
    // If no badges and no distance, remove the entire paragraph
    const dataParagraph = content.querySelector('.marker-popup-data');
    if (dataParagraph && !content.querySelector('.marker-popup-distance')) {
      dataParagraph.remove();
    } else if (metadataContainer) {
      // If distance exists but no badges, just remove the empty metadata span
      metadataContainer.remove();
    }
  }

}

export function MarkerPopup({ title, description, tags = [], distance, spot, searchedTags = [] }) {
  const wrapper = document.createElement('div');
  wrapper.className = 'marker-popup';

  const descriptionHtml = replaceLinkShortcodes(description || '');


  // Sort tags so searched tags appear first
  const sortedTags = sortTags(tags, searchedTags);

  // Parse title for subtitle (split by |)
  let mainTitle = title || '';
  let subtitle = '';
  if (title && title.includes('|')) {
    const parts = title.split('|').map(p => p.trim());
    mainTitle = parts[0] || '';
    subtitle = parts[1] || '';
  }

  // Content wrapper with padding
  const content = document.createElement('div');
  content.className = 'marker-popup-content';
  content.innerHTML = `
    <div class="marker-popup-header">
      ${MARKER_POPUP_DISPLAY.title && mainTitle ? `<div class="marker-popup-header-title"><h3 class="marker-popup-title">${mainTitle}</h3></div>` : ''}
      <div class="marker-popup-header-actions">
        ${MARKER_POPUP_ACTION_BUTTONS.heart ? `<button class="marker-popup-action marker-popup-action-star icon-btn" title="Favorito">${createIconMarkup('star-solid-full', 'icon')}</button>` : ''}
        ${MARKER_POPUP_ACTION_BUTTONS.directions ? `<button class="marker-popup-action marker-popup-action-directions icon-btn" title="Direcciones">${createIconMarkup('diamond-turn-right-solid', 'icon')}</button>` : ''}
        ${MARKER_POPUP_ACTION_BUTTONS.share ? `<button class="marker-popup-action marker-popup-action-share icon-btn" title="Compartir">${createIconMarkup('share-nodes-solid', 'icon')}</button>` : ''}
      </div>
    </div>
    ${MARKER_POPUP_DISPLAY.title && subtitle ? `<p class="marker-popup-subtitle">${subtitle}</p>` : ''}
    ${spot ? `<p class="marker-popup-data">${MARKER_POPUP_DISPLAY.distance && distance ? `<span class="marker-popup-distance">${distance}</span>` : ''}<span class="marker-popup-metadata"></span></p>` : ''}
    ${MARKER_POPUP_DISPLAY.tags && sortedTags.length > 0
      ? `<div class="marker-popup-tags">${sortedTags
        .filter(tag => tag.slug !== "default")
        .map(tag => `<span class="marker-popup-tag">#${tag[`name_${getCurrentLanguage()}`] || tag.name_en || tag.slug}</span>`)
        .join('')
      }</div>`
      : ''}
    ${MARKER_POPUP_DISPLAY.description && descriptionHtml && !(spot && (spot.auto_card === 1 || spot.auto_card === true)) ? `<p class="marker-popup-description">${descriptionHtml}</p>` : ''}
    ${spot && spot.url && !(spot.auto_card === 1 || spot.auto_card === true) ? `<div class="marker-popup-read-more-container"><a href="#" class="marker-popup-read-more-link" data-is-external="${spot.url.trim().startsWith('https://') || spot.url.trim().startsWith('http://') ? 'true' : 'false'}"></a></div>` : ''}
  `;

  // Insert the carousel at the top
  if (spot && spot.images && spot.images.length > 0) {
    MarkerPopupCarrousel({ images: spot.images }).then(carrousel => {
      if (carrousel) {
        wrapper.insertBefore(carrousel, wrapper.firstChild);
      }
    });
  }

  wrapper.appendChild(content);

  // Update button text based on link type (only if not auto_card)
  if (spot && spot.url && !(spot.auto_card === 1 || spot.auto_card === true)) {
    const readMoreLink = content.querySelector('.marker-popup-read-more-link');
    if (readMoreLink) {
      const isExternal = readMoreLink.getAttribute('data-is-external') === 'true';
      const textKey = isExternal ? 'blog.visitWebsite' : 'blog.readMore';
      readMoreLink.textContent = getText(textKey);
    }
  }

  // Build and insert metadata badges (async)
  if (spot) {
    buildMetadataBadges(spot, content).catch(err => {
      console.error('Error building metadata badges:', err);
    });
  }

  // Wire up the star (favorite) button
  const starBtn = content.querySelector('.marker-popup-action-star');
  if (starBtn && spot) {
    const updateStar = (checkConnection = true) => {
      // Cleanup: if popup is closed/removed from DOM, remove listener
      // Only check connection for event updates, not the initial render
      if (checkConnection && !wrapper.isConnected) {
        window.removeEventListener('favoritesChanged', updateStar);
        return;
      }

      const isFav = isFavorite(spot.id);
      // console.log(`[MarkerPopup] updateStar for spot ${spot.id}: isFavorite=${isFav}`); // Debug

      if (isFav) {
        starBtn.classList.add('favorited');
      } else {
        starBtn.classList.remove('favorited');
      }
    };
    updateStar(false); // Initial call: skip connection check
    starBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      await toggleFavorite(spot.id);
      updateStar();
    });
    window.addEventListener('favoritesChanged', updateStar);
  }

  // Wire up the directions button
  const directionsBtn = content.querySelector('.marker-popup-action-directions');
  if (directionsBtn && spot && spot.coordinates) {
    directionsBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      await showDirectionsPopup({
        lat: spot.coordinates.latitude || spot.coords_lat,
        lng: spot.coordinates.longitude || spot.coords_lng,
        cid_googleMaps: spot.cid_googleMaps
      });
    });
  }

  // Wire up the share button
  const shareBtn = content.querySelector('.marker-popup-action-share');
  if (shareBtn && spot && spot.id) {
    shareBtn.addEventListener('click', (e) => {
      e.preventDefault();
      openSharePopup({
        spotId: spot.id,
        spotTitle: title
      });
    });
  }

  // Wire up the "Read more..." link (only if not auto_card)
  const readMoreLink = content.querySelector('.marker-popup-read-more-link');
  if (readMoreLink && spot && spot.url && !(spot.auto_card === 1 || spot.auto_card === true)) {
    const url = spot.url.trim();
    const isExternal = url.startsWith('https://') || url.startsWith('http://');

    if (isExternal) {
      // External link: open in new tab
      readMoreLink.href = url;
      readMoreLink.target = '_blank';
      readMoreLink.rel = 'noopener noreferrer';
    } else {
      // Internal blog: open in popup (current behavior)
      readMoreLink.addEventListener('click', async (e) => {
        e.preventDefault();

        try {
          // Lazy-load blog module only when needed
          const blogModule = await import('../blog/blog-router.js');
          const blogPopupModule = await import('../blog/blog-popup.js');
          const blogContent = await blogModule.loadBlogPost(getCurrentLanguage(), url);
          if (blogContent) {
            // Create and show the blog popup without changing URL
            await blogPopupModule.createBlogPopup({
              language: getCurrentLanguage(),
              postSlug: url,
              content: blogContent,
              url: `/${getCurrentLanguage()}/${url}` // Include URL for SEO updates
            });
          }
        } catch (error) {
          console.error('Error loading blog post:', error);
        }
      });
    }
  }



  return wrapper;
} 